import { useMemo } from 'react';

import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useOptionalTags } from '../contexts/TagsContext';
import useCollectionTags from './useCollectionTags';
import { getCollectionFacetCardKeys, getCollectionFamilyCardIds } from '../utils/collections';
import { subCollectionsOf } from '../utils/collectionFamily';
import { normalizeId } from '../utils/ids';
import { buildFilterFields, tagsFilterField } from '../utils/kqlBuilder';
import { accessibleCardIds, isCardScoped } from '../utils/permissions';

const NO_TAGS = Object.freeze([]);

/**
 * The vocabulary a dashboard's filter is written in (charts A6): the
 * collection list's own (a dashboard's filter is exactly what the viewer's
 * `?q=` on the list would be, keepr-api SCHEMA "The batch run"), over the
 * collection's family of cards narrowed to a card-scoped reader's cards, its
 * facets, element sets and sub-collections, and the `tags` field.
 *
 * `mode` is the tags field's: 'live' for the viewer's own filter in the URL
 * and a personal dashboard's default (private tags too); 'collection' for
 * the default of a dashboard for everyone, which the server validates in
 * that mode (never a private tag).
 */
export default function useDashboardFilterFields(collection, collectionId, { mode = 'live', enabled = true } = {}) {
	const cardDefinitions = useOptionalCardDefinitions();
	const cards = useMemo(() => (Array.isArray(cardDefinitions?.cards) ? cardDefinitions.cards : []), [cardDefinitions?.cards]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const elementSets = cardDefinitions?.elementSets;
	const { tags: tagVocabulary, loaded: tagsLoaded } = useCollectionTags(enabled ? collectionId : null);
	const myPrivateTags = useOptionalTags()?.tags || NO_TAGS;

	const familyIds = useMemo(() => {
		const ids = getCollectionFamilyCardIds(collection, cards);
		if (collection && isCardScoped(collection)) {
			const mine = new Set(accessibleCardIds(collection));
			return ids.size ? new Set([...ids].filter((id) => mine.has(id))) : mine;
		}
		return ids;
	}, [collection, cards]);
	const facetCardKeys = useMemo(() => getCollectionFacetCardKeys(collection), [collection]);
	const familyMembers = useMemo(() => {
		const subs = subCollectionsOf(collection);
		return subs.length && collection ? [{ _id: normalizeId(collection._id), name: collection.name }, ...subs] : [];
	}, [collection]);

	return useMemo(() => {
		if (typeof getElementsForCard !== 'function') return [];
		return [
			...buildFilterFields({ cards, cardIds: familyIds.size ? familyIds : null, getElementsForCard, facetCardKeys, elementSets, family: familyMembers }),
			tagsFilterField({ tags: tagVocabulary || NO_TAGS, privateTags: myPrivateTags, itemTitles: [], mode, loaded: tagsLoaded, quickFilter: false }),
		];
	}, [cards, familyIds, getElementsForCard, facetCardKeys, elementSets, familyMembers, tagVocabulary, myPrivateTags, tagsLoaded, mode]);
}
